import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Cat, Save } from 'lucide-react';
import ImagePicker from '@/components/ImagePicker';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// List View
export function AdminAlumniList() {
  const { getAuthHeader } = useAuth();
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlumni();
  }, []);

  const fetchAlumni = async () => {
    try {
      const response = await fetch(`${API}/alumni`);
      if (response.ok) {
        const data = await response.json();
        setAlumni(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce chaton ?')) return;
    
    try {
      const response = await fetch(`${API}/admin/alumni/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        toast.success('Chaton supprimé');
        fetchAlumni();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="hover:text-sand">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold">Chatons adoptés</h1>
          </div>
          <Link to="/admin/alumni/new">
            <Button className="bg-autumn hover:bg-autumn-dark">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau chaton
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-center text-forest/60">Chargement...</p>
        ) : alumni.length === 0 ? (
          <div className="text-center py-12">
            <Cat className="w-12 h-12 text-forest/30 mx-auto mb-4" />
            <p className="font-body text-forest/60">Aucun chaton adopté enregistré</p>
            <Link to="/admin/alumni/new" className="text-autumn hover:underline mt-2 inline-block">
              Ajouter le premier chaton
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {alumni.map((kitten) => (
              <div key={kitten.id} className="bg-white rounded-xl overflow-hidden shadow-soft">
                {kitten.image_url && (
                  <img src={kitten.image_url} alt={kitten.name} className="w-full h-40 object-cover" />
                )}
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-forest">{kitten.name}</h3>
                  <p className="font-body text-sm text-forest/60">
                    {kitten.adopter_name} • {kitten.adoption_date}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Link to={`/admin/alumni/edit/${kitten.id}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Edit className="w-4 h-4 mr-1" /> Modifier
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(kitten.id)} className="text-red-500 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// Form View
export function AdminAlumniForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    adoption_date: '',
    adopter_name: '',
    adopter_location: 'France',
    image_url: '',
    comment: '',
    parents: 'Sia × Caramel',
    color: 'Sibérien traditionnel'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchAlumni();
    }
  }, [id]);

  const fetchAlumni = async () => {
    try {
      const response = await fetch(`${API}/alumni`);
      if (response.ok) {
        const data = await response.json();
        const kitten = data.find(k => k.id === id);
        if (kitten) {
          setFormData(kitten);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEdit ? `${API}/admin/alumni/${id}` : `${API}/admin/alumni`;
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEdit ? 'Chaton mis à jour' : 'Chaton ajouté');
        navigate('/admin/alumni');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/admin/alumni" className="hover:text-sand">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold">
            {isEdit ? 'Modifier le chaton' : 'Nouveau chaton adopté'}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-soft space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Nom du chaton *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1"
                placeholder="Ex: Akira"
                required
              />
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Date d'adoption *</Label>
              <Input
                value={formData.adoption_date}
                onChange={(e) => setFormData({...formData, adoption_date: e.target.value})}
                className="mt-1"
                placeholder="Ex: Août 2025"
                required
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Nom de l'adoptant *</Label>
              <Input
                value={formData.adopter_name}
                onChange={(e) => setFormData({...formData, adopter_name: e.target.value})}
                className="mt-1"
                placeholder="Ex: Famille Martin"
                required
              />
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Localisation</Label>
              <Input
                value={formData.adopter_location}
                onChange={(e) => setFormData({...formData, adopter_location: e.target.value})}
                className="mt-1"
                placeholder="Ex: Paris, France"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Parents</Label>
              <Input
                value={formData.parents}
                onChange={(e) => setFormData({...formData, parents: e.target.value})}
                className="mt-1"
                placeholder="Ex: Sia × Caramel"
              />
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Couleur</Label>
              <Input
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="mt-1"
                placeholder="Ex: Brown Blotched Tabby"
              />
            </div>
          </div>

          <div>
            <ImagePicker
              label="Photo du chaton *"
              value={formData.image_url}
              onChange={(url) => setFormData({...formData, image_url: url})}
            />
          </div>

          <div>
            <Label className="font-body font-semibold text-forest">Commentaire de l'adoptant</Label>
            <Textarea
              value={formData.comment}
              onChange={(e) => setFormData({...formData, comment: e.target.value})}
              className="mt-1 min-h-[100px]"
              placeholder="Ce que l'adoptant dit de son chaton..."
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading} className="bg-forest hover:bg-forest-dark">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Link to="/admin/alumni">
              <Button type="button" variant="outline">Annuler</Button>
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
